import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Scissors, User, LogOut, Calendar, Menu, X } from 'lucide-react';

const Header = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-black/95 backdrop-blur border-b border-lime-500/30">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_barber-cape-coral/artifacts/b9xk2g3p_761.png" 
              alt="Sauce Sity Logo" 
              className="h-16 w-16 object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-4 xl:space-x-6">
            <Link to="/" className="text-zinc-300 hover:text-lime-400 transition-colors text-sm xl:text-base">
              Home
            </Link>
            <Link to="/services" className="text-zinc-300 hover:text-lime-400 transition-colors text-sm xl:text-base">
              Services
            </Link>
            <Link to="/membership" className="text-zinc-300 hover:text-lime-400 transition-colors text-sm xl:text-base">
              Membership
            </Link>
            <Link to="/rewards" className="text-zinc-300 hover:text-lime-400 transition-colors text-sm xl:text-base">
              Rewards
            </Link>
            <Link to="/leaderboard" className="text-zinc-300 hover:text-lime-400 transition-colors text-sm xl:text-base" data-testid="nav-leaderboard">
              Leaderboard
            </Link>
            <Link to="/referrals" className="text-zinc-300 hover:text-lime-400 transition-colors text-sm xl:text-base">
              Refer & Earn
            </Link>
            <Link to="/gallery" className="text-zinc-300 hover:text-lime-400 transition-colors text-sm xl:text-base">
              Gallery
            </Link>
            <Link to="/share-your-style" className="text-zinc-300 hover:text-lime-400 transition-colors text-sm xl:text-base">
              Share Style
            </Link>
            <Link to="/promotions" className="text-zinc-300 hover:text-lime-400 transition-colors text-sm xl:text-base">
              Promotions
            </Link>
            <Link to="/merch" className="text-zinc-300 hover:text-lime-400 transition-colors text-sm xl:text-base">
              Merch
            </Link>
            {currentUser && currentUser.role === 'customer' && (
              <Link to="/my-appointments" className="text-zinc-300 hover:text-lime-400 transition-colors text-sm xl:text-base">
                Appointments
              </Link>
            )}
            {currentUser && currentUser.role === 'admin' && (
              <Link to="/admin/dashboard" className="text-zinc-300 hover:text-lime-400 transition-colors text-sm xl:text-base">
                Dashboard
              </Link>
            )}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-2 md:space-x-3">
            {currentUser ? (
              <>
                {/* Mobile: Just show first name and logout */}
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1 text-purple-400">
                    <User className="w-4 h-4" />
                    <span className="text-sm font-medium">{currentUser.name.split(' ')[0]}</span>
                  </div>
                  <Button 
                    onClick={handleLogout}
                    size="sm"
                    variant="ghost"
                    className="text-zinc-400 hover:text-lime-400"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button size="sm" variant="ghost" className="text-zinc-300 hover:text-lime-400 text-xs sm:text-sm">
                    Sign In
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button size="sm" className="bg-gradient-to-r from-lime-500 to-lime-600 text-black font-semibold hover:from-lime-600 hover:to-lime-700">
                    Sign Up
                  </Button>
                </Link>
              </>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-lime-400 hover:text-lime-300"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-zinc-800 pt-4">
            {/* Book Button for logged-in customers */}
            {currentUser && currentUser.role === 'customer' && (
              <Link 
                to="/booking" 
                onClick={() => setMobileMenuOpen(false)}
                className="block mb-4"
              >
                <Button className="w-full bg-gradient-to-r from-lime-500 to-lime-600 text-black font-semibold hover:from-lime-600 hover:to-lime-700">
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Appointment
                </Button>
              </Link>
            )}
            
            <nav className="flex flex-col space-y-3">
              <Link 
                to="/" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-zinc-300 hover:text-lime-400 transition-colors py-2"
              >
                Home
              </Link>
              <Link 
                to="/services" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-zinc-300 hover:text-lime-400 transition-colors py-2"
              >
                Services
              </Link>
              <Link 
                to="/membership" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-zinc-300 hover:text-lime-400 transition-colors py-2"
              >
                Membership
              </Link>
              <Link 
                to="/rewards" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-zinc-300 hover:text-lime-400 transition-colors py-2"
              >
                Rewards
              </Link>
              <Link
                to="/leaderboard"
                onClick={() => setMobileMenuOpen(false)}
                className="text-zinc-300 hover:text-lime-400 transition-colors py-2"
                data-testid="nav-leaderboard-mobile"
              >
                Leaderboard
              </Link>
              <Link 
                to="/referrals" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-zinc-300 hover:text-lime-400 transition-colors py-2"
              >
                Refer & Earn
              </Link>
              <Link 
                to="/gallery" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-zinc-300 hover:text-lime-400 transition-colors py-2"
              >
                Gallery
              </Link>
              <Link 
                to="/share-your-style" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-zinc-300 hover:text-lime-400 transition-colors py-2"
              >
                Share Your Style
              </Link>
              <Link 
                to="/promotions" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-zinc-300 hover:text-lime-400 transition-colors py-2"
              >
                Promotions
              </Link>
              <Link 
                to="/merch" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-zinc-300 hover:text-lime-400 transition-colors py-2"
              >
                Merch
              </Link>
              {currentUser && currentUser.role === 'customer' && (
                <>
                  <Link 
                    to="/my-appointments" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-zinc-300 hover:text-lime-400 transition-colors py-2"
                  >
                    My Appointments
                  </Link>
                  <Link 
                    to="/booking" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-zinc-300 hover:text-lime-400 transition-colors py-2"
                  >
                    Book Appointment
                  </Link>
                </>
              )}
              {currentUser && currentUser.role === 'admin' && (
                <Link 
                  to="/admin/dashboard" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-zinc-300 hover:text-lime-400 transition-colors py-2"
                >
                  Admin Dashboard
                </Link>
              )}
              {!currentUser && (
                <Link 
                  to="/login" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-zinc-300 hover:text-lime-400 transition-colors py-2"
                >
                  Sign In
                </Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
